import React, { useEffect, useState } from "react";
import { api } from "@/lib/auth";
import { PageHeader } from "@/pages/Classes";
import { Play, X } from "lucide-react";

const isVideoUrl = (u) => typeof u === "string" && /\.(mp4|webm|mov|quicktime)(\?|$)/i.test(u);
const thumbUrl = (u) => (typeof u === "string" && /\/api\/files\/[A-Za-z0-9-]+$/.test(u) ? `${u}?size=thumb` : u);
const isEmbed = (u) => typeof u === "string" && /(youtube\.com|youtu\.be|vimeo\.com)/i.test(u);

function toEmbedUrl(u) {
  if (!u) return u;
  const yt = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?autoplay=1`;
  const vim = u.match(/vimeo\.com\/(\d+)/);
  if (vim) return `https://player.vimeo.com/video/${vim[1]}?autoplay=1`;
  return u;
}

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [viewing, setViewing] = useState(null);

  useEffect(() => { api.get("/gallery").then(r => setItems(r.data)); }, []);

  // Close on ESC
  useEffect(() => {
    if (!viewing) return;
    const onKey = (e) => { if (e.key === "Escape") setViewing(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewing]);

  const kindOf = (g) => {
    if (g.video_url && isEmbed(g.video_url)) return "embed";
    if (g.video_url || isVideoUrl(g.image)) return "video";
    return "image";
  };

  return (
    <>
      <PageHeader eyebrow="Moments" title="Gallery" />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-12 columns-2 md:columns-3 lg:columns-4 gap-2 space-y-2" data-testid="gallery-grid">
        {items.map((g, i) => {
          const kind = kindOf(g);
          const src = g.video_url || g.image;
          return (
            <button
              key={g.id}
              type="button"
              onClick={() => setViewing(g)}
              className="break-inside-avoid overflow-hidden card-hover relative w-full block group"
              data-testid={`gallery-${i}`}
              aria-label="Open"
            >
              {kind === "image" ? (
                <img src={thumbUrl(g.image)} loading="lazy" decoding="async" alt="" className="w-full object-cover group-hover:scale-105 transition-transform duration-500" />
              ) : (
                <>
                  <video src={src} className="w-full object-cover" muted playsInline preload="metadata" data-testid={`gallery-video-${i}`} />
                  <div className="absolute inset-0 grid place-items-center bg-black/30 group-hover:bg-black/10 transition-all">
                    <div className="w-14 h-14 rounded-full bg-red-600 grid place-items-center shadow-lg"><Play size={22} className="text-white ml-1" /></div>
                  </div>
                </>
              )}
            </button>
          );
        })}
      </div>

      {viewing && (
        <div
          className="fixed inset-0 bg-black/95 z-50 grid place-items-center p-6"
          onClick={() => setViewing(null)}
          data-testid="gallery-viewer"
        >
          <button
            className="absolute top-6 right-6 text-white hover:text-red-500 transition-colors"
            onClick={() => setViewing(null)}
            aria-label="Close"
            data-testid="gallery-viewer-close"
          >
            <X size={28} />
          </button>
          <div
            className="w-full max-w-5xl max-h-[85vh] flex items-center justify-center"
            onClick={e => e.stopPropagation()}
          >
            {kindOf(viewing) === "embed" ? (
              <div className="w-full aspect-video">
                <iframe
                  src={toEmbedUrl(viewing.video_url)}
                  className="w-full h-full"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title="video"
                />
              </div>
            ) : kindOf(viewing) === "video" ? (
              <video
                src={viewing.video_url || viewing.image}
                className="max-w-full max-h-[85vh]"
                controls
                autoPlay
                playsInline
                data-testid="gallery-viewer-video"
              />
            ) : (
              <img
                src={viewing.image}
                alt=""
                className="max-w-full max-h-[85vh] object-contain"
                data-testid="gallery-viewer-image"
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}
